import { useCallback, useEffect, useMemo, useState } from 'react'

// Components
import { Grid } from 'components/grid'
import { Range } from 'components/range'
import { Info } from 'components/info'
import { TradeInput } from 'components/tradePair'
import { Warning } from 'components/warning'
//import { CoinIcon } from 'components/coinIcon'
//import { Skeleton } from 'components/skeleton'

// Styles
import {
  OwnedCard,
  OwnedContent,
  SectionButton,
  StatFieldCard,
  Percentage,
  Rewards,
  SectionTitle,
  WillingToClaimContainer,
  WillingToClaimValue,
  UserStats,
  RewardsSeparator,
  StatsSeparator,
  UserTitle,
  UserAccount,
  UnholdForm,
  RangeContainer,
  DualSection,
  RewardsSection,
  //CustomStatCard,
  //CustomStatCardTitle,
  //CustomStatPill,
  //CustomStatCardContent,
  //CustomStatCardAmount,
  ButtonContainer,
  //PillContainer,
  Separator,
  RateLabel,
  UnholdLabel,
  CompoundButton,
  HoldingsCard,
  SellSwitchContainer,
  SellSwitch,
} from './ownedCrypto.styled'

// Utils
import { numberFormatter } from 'utils'

// Hooks
import { SafePoktUserParams, SafePoktParams } from 'hooks/useSafePOKT'
import { useSafePOKTContext } from 'providers/SafePOKTProvider'
import { useToasts } from 'providers/ToastProvider'
import { useReady, useBridge } from 'hooks/useReady'
import { useWalletContext } from 'providers/WalletProvider'
import { useWpoktMintControllerContext } from 'providers/WpoktMintControllerProvider'
import { useSpendableTokenContext } from 'providers/SpendableTokenProvider'
// Constants
import {
  CLAIMABLE_INFO,
  //CLAIM_RATE_TITLE_INFO,
  ERROR_CLAIM_RATE_TOAST,
  ERROR_CLAIM_TOAST,
  ERROR_COMPOUND_TOAST,
  ERROR_UNHOLD_TOAST,
  ERROR_SELLORDER_TOAST,
  HOLDINGS_RELEASED_INFO,
  POKT_HOLDINGS_INFO,
  SUCCESS_CLAIM_RATE_TOAST,
  SUCCESS_CLAIM_TOAST,
  SUCCESS_COMPOUND_TOAST,
  SUCCESS_UNHOLD_TOAST,
  SUCCESS_SELLORDER_TOAST,
  SUCCESS_WPOKT_CLAIMED_TOAST,
  ERROR_WPOKT_CLAIMED_TOAST,
  ERROR_USER_WPOKT_CLAIMED_TOAST,
  UNHOLDING_INFO,
  SELL_INFO,
  CURRENT_SELL_INFO,
  getWPOKT_CLAIMED_TOAST,
  FINISH_WPOKT_CLAIM_TOAST,
} from './ownedCrypto.constants'

// Type
import { TransactionResponse } from '@ethersproject/abstract-provider'

// Utils
import {
  getClaimRateInfo,
  getClaimWarningToast,
  getDisabledInfo,
  getDisabledInfoEth,
  timeout,
  fetchWpoktMintData,
} from './ownedCrypto.utils'
import { sharesToNodes } from 'hooks/useSafePOKT/useSafePOKT.utils'

interface OwnedCryproProps {
  isDisabled: boolean
  isLoading: boolean
}

export const OwnedCrypto = ({ isDisabled, isLoading }: OwnedCryproProps) => {
  const [willingToClaim, setWillingToClaim] = useState(100)
  const [amount, setAmount] = useState('0')
  const [amountSell, setAmountSell] = useState('0')
  const [isTransacting, setIsTransacting] = useState<
    'none' | 'claiming' | 'updating' | 'unholding' | 'compounding' | 'selling' | 'receivingWPOKT' | 'swappingChain' | 'confirmingSell'
  >('none')

  const isReady = useReady()
  const isBridge = useBridge()

  const { newToast, popToast } = useToasts()
  const safePoktState = useSafePOKTContext()
  const { currentAccount, chainId, swapToTarget, changeNetwork, provider, ethBridgeNetwork } = useWalletContext()
  const { refreshParamsUSDC, refreshParamsWpoktEth } = useSpendableTokenContext()
  const { mintWpokt, isLoadingMint } = useWpoktMintControllerContext()

  const [switchValue, setSwitchValue] = useState(false)

  useEffect(() => {
    setWillingToClaim(safePoktState.exists ? safePoktState.willingToClaim : 100)
  }, [safePoktState.willingToClaim, safePoktState.exists])

  const onPercentageSubmit = useCallback(async () => {
    if (isReady) {
      try {
        newToast(
          getClaimWarningToast(willingToClaim, 100 - willingToClaim),
          undefined,
          true
        )
        setIsTransacting('updating')
        const claimTransaction = await safePoktState.changeClaimPercent(
          willingToClaim
        )
        popToast()
        newToast(SUCCESS_CLAIM_RATE_TOAST, claimTransaction.hash)

        setIsTransacting('none')
      } catch (error) {
        console.error(error)
        setIsTransacting('none')
        popToast()
        newToast(ERROR_CLAIM_RATE_TOAST, (error as TransactionResponse).hash)
      }
    }
  }, [safePoktState, isReady, willingToClaim, newToast, popToast])

  const onClaim = useCallback(async () => {
    try {
      setIsTransacting('claiming')
      const claimTransaction = await safePoktState.claimReward()
      refreshParamsUSDC()
      newToast(SUCCESS_CLAIM_TOAST, claimTransaction.hash)
      setIsTransacting('none')
    } catch (error) {
      console.error(error)
      newToast(ERROR_CLAIM_TOAST, (error as TransactionResponse).hash)
      setIsTransacting('none')
    }
  }, [safePoktState, refreshParamsUSDC, newToast])

  const onCompoundAll = useCallback(async () => {
    try {
      setIsTransacting('compounding')
      const claimTransaction = await safePoktState.compoundPoktReward()
      newToast(SUCCESS_COMPOUND_TOAST, claimTransaction.hash)
      setIsTransacting('none')
    } catch (error) {
      console.error(error)
      newToast(ERROR_COMPOUND_TOAST, (error as TransactionResponse).hash)
      setIsTransacting('none')
    }
  }, [safePoktState, newToast])

  const onUnholdSubmit = useCallback(
    async (amount: string) => {
      try {
        setIsTransacting('unholding')
        const unHoldTransaction = await safePoktState.unHoldPoktOrder(
          Math.min(
            Number(safePoktState[SafePoktUserParams.PoktReward]),
            Number(amount)
          )
        )
        newToast(SUCCESS_UNHOLD_TOAST, unHoldTransaction.hash)
        setIsTransacting('none')
        setAmount('0')
      } catch (error) {
        console.error(error)
        newToast(ERROR_UNHOLD_TOAST, (error as TransactionResponse).hash)
        setIsTransacting('none')
      }
    },
    [safePoktState, newToast]
  )
  const onSellSubmit = useCallback(
    async (amount: string) => {
        try {
            setIsTransacting('selling')
            const sellTransaction = await safePoktState.sellSharesOrder(
                Math.min(
                    Number(safePoktState[SafePoktUserParams.PoktShareCount]),
                    Number(amount)
                )
            )
            newToast(SUCCESS_SELLORDER_TOAST, sellTransaction.hash)
            setIsTransacting('none')
            setAmountSell('0')
        } catch (error) {
            console.error(error)
            newToast(ERROR_SELLORDER_TOAST, (error as TransactionResponse).hash)
            setIsTransacting('none')
        }
    },
    [safePoktState, newToast]
  )

    const swapWPOKT = useCallback(
        async () => {
            if (isLoadingMint) return 
            let toastErrorException = ERROR_WPOKT_CLAIMED_TOAST;
            try {      
                //if (chainId == `0x${Number(targetNetwork.chainParams.chainId).toString(16)}`         
                //await changeNetwork(provider, ethBridgeNetwork.chainParams)
                setIsTransacting('receivingWPOKT')
                // Gather ACTIVE mints from safepokt
                const mints = await fetchWpoktMintData(currentAccount, true)
                let safePoktMintPos = undefined;
                for (let i = 0; i < mints.length; ++i) {
                    if (mints[i]['data'] && mints[i]['signatures'] && mints[i]['sender_address'] == "9fd376497d96792ac239354ff663a24a8b2d53b2") {
                      //console.log("ID: "+parseInt(mints[i]['_id'],16).toString()+"--- "+parseInt(mints[i]['_id'],16)+" - sellepoch: "+safePoktState[SafePoktUserParams.SellEpoch])
                      if (safePoktState[SafePoktUserParams.SellEpoch] == 1 || parseInt(mints[i]['_id'],16).toString() === safePoktState[SafePoktUserParams.SellEpoch].toString()) {
                        safePoktMintPos = i
                        console.log("[+] Mint ready to claim -> let's claim")
                        break
                      }
                    }
                }
                let mintTransaction = undefined
                //console.log("isLoadingMint: "+isLoadingMint+" -- safepoktMintpos: "+safePoktMintPos)
                if (safePoktMintPos !== undefined) {
                    // Active mint is found! -> Lets mint the WPOKT in ethereum network
                    //console.log(mints[safePoktMintPos]);
                    const mintData = {
                        recipient: currentAccount,
                        amount: mints[safePoktMintPos]['data']['amount'],
                        nonce: mints[safePoktMintPos]['data']['nonce']
                    }
                    const signatures = mints[safePoktMintPos]['signatures']
                    mintTransaction = await mintWpokt(mintData, signatures)
                    newToast(
                        getWPOKT_CLAIMED_TOAST,
                        mintTransaction.hash,
                        true
                    )

                } else {
                    newToast(
                        FINISH_WPOKT_CLAIM_TOAST,
                        undefined,
                        true
                    )
                }
                toastErrorException = ERROR_USER_WPOKT_CLAIMED_TOAST
                let mintFound = false
                if (mintTransaction !== undefined && mintTransaction.hash) { 
                  console.log("[+] Found mint TX hash -> lets confirm to sell again!"); 
                  mintFound = true;
                } else {
                  const allMints = await fetchWpoktMintData(currentAccount, false)
                  for (let i = 0; i < allMints.length; ++i) {
                    if (allMints[i]['data'] && allMints[i]['signatures'] && allMints[i]['sender_address'] == "9fd376497d96792ac239354ff663a24a8b2d53b2" && allMints[i]['status'] == "success") {
                      if (parseInt(allMints[i]['_id'],16).toString() === safePoktState[SafePoktUserParams.SellEpoch].toString()) {
                        mintFound = true
                        console.log("[+] Found idMint -> sell is claimed, lets confirm to sell again!")
                        break
                      } else if (safePoktState[SafePoktUserParams.SellEpoch] == 1) {
                        mintFound = true
                        console.log("[+] Found at least 1 mint from safepokt -> sell is claimed, lets confirm to sell again!")
                        break
                      }                      
                    }
                  }
                }             
                                
                if (mintFound) {
                  setIsTransacting('swappingChain')
                  if (swapToTarget && chainId === '0x1') await swapToTarget(true)
                  await timeout(1500)
                  setIsTransacting('confirmingSell')
                  const confirmSellClaimedTransaction = await safePoktState.confirmSellOrderClaimed()
                  refreshParamsWpoktEth()
                  popToast()
                  newToast(SUCCESS_WPOKT_CLAIMED_TOAST, confirmSellClaimedTransaction.hash)
                  setIsTransacting('none')
                } else {
                  newToast(ERROR_USER_WPOKT_CLAIMED_TOAST, undefined, true)
                }

                
            } catch (error) {
                console.error(error)
                popToast()
                newToast(toastErrorException, (error as TransactionResponse).hash)
                setIsTransacting('none')
            }
        },
        [safePoktState, currentAccount, isLoadingMint, mintWpokt, refreshParamsWpoktEth, swapToTarget, chainId, changeNetwork, newToast, popToast]
    )

  const rateDisabledInfo = useMemo(
    () =>
      !isLoading &&
      getDisabledInfo(
        isBridge,
        safePoktState.exists,
        !safePoktState.holderActionsEnabled
      ),
      [isLoading, isBridge, safePoktState.exists, safePoktState.holderActionsEnabled]
  )

  const unholdDisabledInfo = useMemo(
    () =>
      !isLoading &&
      getDisabledInfo(
        isBridge,
        safePoktState.exists,
        !safePoktState.holderActionsEnabled
      ),
    [isLoading, isBridge, safePoktState.exists, safePoktState.holderActionsEnabled]
  )

  const claimWpoktDisabledInfo = useMemo(
    () =>
        !isLoading && 
        (isTransacting !== 'confirmingSell') &&
        getDisabledInfoEth(
            (safePoktState[SafePoktUserParams.SellEpoch] !== 0) && !isBridge,
        ),
    [isLoading, isTransacting, isBridge, safePoktState]
  )

  const claimDisabledInfo = useMemo(
    () =>
      !isLoading &&
      getDisabledInfo(
        isBridge,
        safePoktState.exists,
        !safePoktState.holderActionsEnabled,
        safePoktState.noRewardThisEpoch,
        !safePoktState.canClaimReward
      ),
    [
      isLoading,
      isBridge,
      safePoktState.exists,
      safePoktState.holderActionsEnabled,
      safePoktState.noRewardThisEpoch,
      safePoktState.canClaimReward,
    ]
  )

  const handleSwitchValueChange = (value: boolean) => {
    setSwitchValue(value);
    if (!switchValue) { //ON
    } else { //OFF
    }
  }

  return (
    <OwnedCard>
      <OwnedContent>
        <Percentage>
          <div>
            <SectionTitle>
              Claim Rate
                          {/* <Info text={CLAIM_RATE_TITLE_INFO} /> */}
            </SectionTitle>
            <WillingToClaimContainer>
              <StatFieldCard
                disabled={isDisabled}
                isLoading={isLoading}
                label='Current Rate'
                info={getClaimRateInfo(
                  safePoktState[SafePoktUserParams.WillingToClaim],
                  100 - safePoktState[SafePoktUserParams.WillingToClaim]
                )}
                stat={{
                  value: numberFormatter({
                    number: safePoktState[SafePoktUserParams.WillingToClaim],
                    unit: '%',
                  }),
                }}
              />

              <RateLabel>Update Rate</RateLabel>
              <WillingToClaimValue>{willingToClaim}%</WillingToClaimValue>

              <RangeContainer>
                <Range
                  min={0}
                  max={100}
                  step={1}
                  disabled={
                    isDisabled ||
                    isTransacting !== 'none' ||
                    !safePoktState[SafePoktUserParams.CanUpdatePercent]
                  }
                  value={willingToClaim}
                  onChange={(e) => setWillingToClaim(e)}
                />
              </RangeContainer>
            </WillingToClaimContainer>
          </div>
          <ButtonContainer>
            {rateDisabledInfo && <Warning>{rateDisabledInfo}</Warning>}
            <SectionButton
              disabled={
                isDisabled ||
                safePoktState[SafePoktUserParams.WillingToClaim] ===
                  willingToClaim ||
                isTransacting !== 'none' ||
                !safePoktState[SafePoktUserParams.CanUpdatePercent]
              }
              onClick={onPercentageSubmit}
            >
              {isTransacting === 'updating'
                ? 'Setting Claim Rate...'
                : 'Set Claim Rate'}
            </SectionButton>
          </ButtonContainer>
        </Percentage>
        <RewardsSeparator />
        <UserStats>
          <div>
            <UserTitle hasAccount={!!currentAccount}>
              Holder
              {currentAccount && <UserAccount account={currentAccount} />}
            </UserTitle>
            <Grid breakpoints={{ xs: 1, s: 1, l: 1, xll: 1 }}>
              <StatFieldCard
                disabled={isDisabled}
                isLoading={isLoading}
                isHighlighted
                label='Shares Owned'
                stat={{
                  value: numberFormatter({
                    number: safePoktState[SafePoktUserParams.PoktShareCount],
                  }),
                }}
                subStat={`${numberFormatter({
                  number: sharesToNodes(
                      safePoktState[SafePoktUserParams.PoktShareCount],
                      safePoktState[SafePoktParams.PoktPerShare]
                  ),
                })} Nodes`}
              />
              <StatFieldCard
                disabled={isDisabled}
                isLoading={isLoading}
                label='Revenue'
                stat={{
                  token: { image: 'USDC', unit: '' },
                  value: numberFormatter({
                    number: safePoktState[SafePoktUserParams.TotalClaimedUSDC],
                  }),
                }}
              />
              <StatFieldCard
                disabled={isDisabled}
                isLoading={isLoading}
                label='Next Payout'
                info='Next distribution POKT reward estimation.'
                stat={{
                  token: { image: 'POKT', unit: '' },
                  value: numberFormatter({
                    number: (safePoktState[SafePoktUserParams.LatestSnapshotIndex] <= safePoktState[SafePoktParams.LatestSnapshotIndex])?( safePoktState[SafePoktUserParams.PoktShareCount] * safePoktState[SafePoktParams.NextEpochRPS] ):0,
                  }),
                }}
              />
           {/*   
              <CustomStatCard>
                <CustomStatCardTitle>Next Payout</CustomStatCardTitle>  <Info text='Next distribution POKT reward estimation' />
                <CustomStatCardContent>
                 <PillContainer>
                    <CustomStatPill>
                      <CoinIcon coinImage='POKT' />
                      <CustomStatCardAmount>
                        {isLoading ? (
                          <Skeleton />
                        ) : (
                          numberFormatter({
                            number: parseInt(
                              safePoktState[
                                SafePoktUserParams.PoktReward
                              ].toString()
                            ),
                          })
                        )}
                      </CustomStatCardAmount>
                   </CustomStatPill>
                 </PillContainer> 
                 */}
                  {/*
                    <PillContainer>
                    <CustomStatPill>
                      <CoinIcon coinImage='USDC' />
                      <CustomStatCardAmount>
                        {isLoading ? (
                          <Skeleton />
                        ) : (
                          numberFormatter({
                            number: parseInt(
                              safePoktState[
                                SafePoktUserParams.RewardClaimable
                              ].toString()
                            ),
                          })
                        )}
                      </CustomStatCardAmount>
                    </CustomStatPill>
                  </PillContainer>                  
                </CustomStatCardContent>
              </CustomStatCard>*/}
            </Grid>
          </div>
        </UserStats>
        <StatsSeparator />
        <RewardsSection>
          <SectionTitle>Management</SectionTitle>
          <DualSection>
            <UnholdForm>
              <div>
                <Grid breakpoints={{ xs: 1, s: 1, l: 1, xll: 1 }}>
                  <HoldingsCard
                    disabled={isDisabled}
                    label='POKT Rewards'
                    info={POKT_HOLDINGS_INFO}
                    isLoading={isLoading}
                    stat={{
                      value: numberFormatter({
                        number: safePoktState[SafePoktUserParams.PoktReward],
                      }),
                      token: { image: 'POKT', unit: '' },
                    }}
                  >
                    <CompoundButton
                      disabled={
                        isDisabled ||
                        isTransacting !== 'none' ||
                        safePoktState[SafePoktUserParams.PoktReward] === 0
                      }
                      onClick={onCompoundAll}
                    >
                      {isTransacting === 'compounding'
                        ? 'Staking...'
                        : 'Stake All'}
                    </CompoundButton>
                  </HoldingsCard>
                <StatFieldCard
                    disabled={isDisabled}
                    isHighlighted={true}
                    isLoading={isLoading}
                    label='Claimable Rewards'
                    info={CLAIMABLE_INFO}
                    stat={{
                        value: numberFormatter({
                        number:
                            safePoktState[SafePoktUserParams.RewardClaimable],
                        }),
                        token: { image: 'USDC', unit: 'USDC' },
                    }}
                />
                </Grid>
              </div>
              <ButtonContainer>
                {claimDisabledInfo && <Warning>{claimDisabledInfo}</Warning>}
                <SectionButton
                onClick={onClaim}
                disabled={
                    isDisabled ||
                    safePoktState[SafePoktUserParams.RewardClaimable] === 0 ||
                    !safePoktState[SafePoktUserParams.CanClaimRewards] ||
                    safePoktState[SafePoktUserParams.NoRewardThisEpoch] ||
                    isTransacting !== 'none'
                }
                >
                {isTransacting === 'claiming'
                    ? 'Claiming Rewards...'
                    : 'Claim Rewards'}
                </SectionButton>
              </ButtonContainer>

              </UnholdForm>
            <Separator />
            <SellSwitchContainer>
               <SellSwitch labelOn="SELL" labelOff="SWEEP" onChange={handleSwitchValueChange} />
            </SellSwitchContainer>
            { (!switchValue) ? (
            <>
            <Rewards onSubmit={(e) => {
                e.preventDefault()
                onUnholdSubmit(amount)
              }}>
              <div>
                <Grid breakpoints={{ xs: 1, s: 1, l: 1, xll: 1 }}>
                  <StatFieldCard
                    disabled={isDisabled}
                    label='Current Sweeps'
                    info={HOLDINGS_RELEASED_INFO}
                    isLoading={isLoading}
                    stat={{
                      value: numberFormatter({
                        number: safePoktState[SafePoktUserParams.PoktUnHold],
                      }),
                      token: { image: 'POKT', unit: '' },
                    }}
                    subStat={
                      safePoktState[SafePoktUserParams.PoktUnHold] === 0
                        ? ''
                        : safePoktState[SafePoktUserParams.PoktUnHoldClaimable]
                        ? 'Claimable!'
                        : 'Claim: Next epoch'
                    }
                  />

                </Grid>
              </div>
              <div>
                <UnholdLabel>
                    Sweep Rewards <Info text={UNHOLDING_INFO} />
                </UnholdLabel>
                <TradeInput
                    small
                    step={0.01}
                    disabled={isDisabled || safePoktState[SafePoktUserParams.PoktUnHold] !== 0}
                    coinImage='POKT'
                    coinName='POKT'
                    min={0}
                    value={amount}
                    onChange={(e) => {
                        const newValue = e.target.value
                        const roundedNewValue = newValue
                            ? (Math.round((Number(newValue) + Number.EPSILON) * 100) / 100)
                            : newValue
                        const max = safePoktState[SafePoktUserParams.PoktReward] + Number(0.01)
                        if (roundedNewValue > max) setAmount( (max.toFixed(2)).toString() )
                        else setAmount(roundedNewValue.toString())
                    }}
                 />

              <ButtonContainer>
                {unholdDisabledInfo && <Warning>{unholdDisabledInfo}</Warning>}
                <SectionButton
                type='submit'
                disabled={
                    isDisabled ||
                    !amount ||
                    amount === '0' ||
                    isTransacting !== 'none' ||
                    safePoktState[SafePoktUserParams.PoktUnHold] !== 0 ||
                    safePoktState[SafePoktUserParams.PoktReward] === 0
                }
                >
                {isTransacting === 'unholding'
                    ? 'Creating Order...'
                    : 'Sweep Order'}
                </SectionButton>
              </ButtonContainer>
              </div>
              </Rewards>
              </>
              ) : (
              <>
                <Rewards onSubmit={(e) => {
                    e.preventDefault()
                    onSellSubmit(amountSell)
                }}>
                    <div>
                        <Grid breakpoints={{ xs: 1, s: 1, l: 1, xll: 1 }}>
                            <StatFieldCard
                                disabled={isLoading}
                                label='Current Sells'
                                info={CURRENT_SELL_INFO}
                                isLoading={isLoading}
                                stat={{
                                    value: numberFormatter({
                                        number: Math.max(
                                            Number(safePoktState[SafePoktUserParams.SellShareAmount])-2,
                                            Number(0)
                                        )
                                    }),
                                    token: { image: 'WPOKT', unit: '' },
                                }}
                                subStat={
                                    safePoktState[SafePoktUserParams.SellShareAmount] === 0
                                        ? ''
                                        : safePoktState[SafePoktUserParams.SellEpoch] !== 0
                                            ? 'Sell ready!'
                                            : 'Sell in process'
                                }
                            />

                        </Grid>
                    </div>
                    <div>
                        <UnholdLabel>
                            Sell Shares <Info text={SELL_INFO} />
                        </UnholdLabel>
                        <TradeInput
                            small
                            step={0.01}
                            disabled={isDisabled || safePoktState[SafePoktUserParams.SellShareAmount] !== 0}
                            coinImage='POKT'
                            coinName='SHARES'
                            min={0}
                            value={amountSell}
                            onChange={(e) => {
                                const newValue = e.target.value
                                const roundedNewValue = newValue
                                    ? (Math.round((Number(newValue) + Number.EPSILON) * 100) / 100)
                                    : newValue
                                const max = safePoktState[SafePoktUserParams.PoktShareCount] + Number(0.01)
                                if (roundedNewValue > max) setAmountSell((max.toFixed(2)).toString())
                                else setAmountSell(roundedNewValue.toString())
                            }}
                        />
                        { (safePoktState[SafePoktUserParams.SellShareAmount] === 0) ? ( 
                        <>
                            <ButtonContainer>
                                {unholdDisabledInfo && <Warning>{unholdDisabledInfo}</Warning>}
                                <SectionButton
                                    type='submit'
                                    disabled={
                                        isDisabled ||
                                        !amountSell ||
                                        amountSell === '0' ||
                                        isTransacting !== 'none' ||
                                        safePoktState[SafePoktUserParams.PoktShareCount] === 0 ||
                                        safePoktState[SafePoktUserParams.SellShareAmount] !== 0 ||
                                        safePoktState[SafePoktUserParams.LatestSnapshotIndex] > safePoktState[SafePoktParams.LatestSnapshotIndex]
                                    }
                                >
                                    {isTransacting === 'selling'
                                        ? 'Creating Order...'
                                        : 'Sell Order'}
                                </SectionButton>
                            </ButtonContainer>
                        </>
                        ) : (
                        <>
                            <ButtonContainer>
                                {claimWpoktDisabledInfo && <Warning>{claimWpoktDisabledInfo}</Warning>}
                                <SectionButton
                                    onClick={(e) => { 
                                        e.preventDefault()
                                        swapWPOKT()
                                    }}
                                    disabled={
                                        chainId !== '0x1' ||
                                        safePoktState[SafePoktUserParams.SellEpoch] === 0
                                    }
                                >
                                    {(isTransacting === 'receivingWPOKT' ? 'Receiving...' :
                                       (isTransacting === 'swappingChain' ? 'Swapping chain...' :
                                          (isTransacting === 'confirmingSell' ? 'Confirming sell...' : 'Receive WPOKT')                    
                                        )
                                     )                                    
                                    }
                                </SectionButton>
                            </ButtonContainer>
                        </> )}

                    </div>
                </Rewards>
              </>               
              )}
          </DualSection>
        </RewardsSection>
      </OwnedContent>
    </OwnedCard>
  )
}
